// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\BerthReservation\ApiBerthReservation.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { BerthReservationSelf, BerthReservationSelfData, BerthReservationSelfOptions } from 'data/models/BerthReservationSelf';
import { BerthReservationVersion, BerthReservationVersionData, BerthReservationVersionOptions } from 'data/models/BerthReservationVersion';
import { CargoActivity, CargoActivityData, CargoActivityOptions } from 'data/models/CargoActivity';
import { Change, ChangeData, ChangeOptions } from 'data/models/Change';
import { CruiseActivity, CruiseActivityData, CruiseActivityOptions } from 'data/models/CruiseActivity';
import { Issue, IssueData, IssueOptions } from 'data/models/Issue';
import { ItineraryPoint, ItineraryPointData, ItineraryPointOptions } from 'data/models/ItineraryPoint';
import { Measurement, MeasurementData, MeasurementOptions } from 'data/models/Measurement';
import { RaftedVessel, RaftedVesselData, RaftedVesselOptions } from 'data/models/RaftedVessel';
import { BerthReservationStatus, VesselSide } from 'server/enums/BerthReservation';

// BerthReservationData is the class that BerthReservation serializes to/from
export interface BerthReservationData {
    Id : number;
    Url : string | null;
	
    CargoActivities : CargoActivityData[] | null;
    Change : ChangeData | null;
    ChangeId : number;
    CruiseActivities : CruiseActivityData[] | null;
    Issues : IssueData[] | null;
    Itinerary : ItineraryPointData | null;
    OthersIssues : IssueData[] | null;
    RaftedVessels : RaftedVesselData[] | null;
    Self : BerthReservationSelfData | null;
    Version : BerthReservationVersionData | null;
	
    ApprovedDate : /* ISO8601 */ string | null;
    Bookable : boolean;
    Cancellable : boolean;
    CancellationRemarks : string | null;
    CancelledDate : /* ISO8601 */ string | null;
    ConflictCount : number;
    Footmark : MeasurementData;
    Guid : /* GUID */ string;
    HasLayOpsOnly : boolean;
    Length : MeasurementData;
    Rafting : boolean;
    RequestDate : /* ISO8601 */ string;
    SideTo : VesselSide;
    Status : BerthReservationStatus;
}

// BerthReservationOptions is the class which may be passed to the BerthReservation constructor to define optional initial data values
export interface BerthReservationOptions {
    Id : number;
    Url? : string | null;
	
    CargoActivities? : CargoActivityOptions[] | null;
    Change? : ChangeOptions | null;
    ChangeId? : number;
    CruiseActivities? : CruiseActivityOptions[] | null;
    Issues? : IssueOptions[] | null;
    Itinerary? : ItineraryPointOptions | null;
    OthersIssues? : IssueOptions[] | null;
    RaftedVessels? : RaftedVesselOptions[] | null;
    Self? : BerthReservationSelfOptions | null;
    Version? : BerthReservationVersionOptions | null;
	
    ApprovedDate? : /* ISO8601 */ string | null;
    CancellationRemarks? : string | null;
    CancelledDate? : /* ISO8601 */ string | null;
    ConflictCount? : number;
    Footmark? : MeasurementOptions;
    Guid? : /* GUID */ string;
    HasLayOpsOnly? : boolean;
    Length? : MeasurementOptions;
    Rafting? : boolean;
    RequestDate? : /* ISO8601 */ string;
    SideTo? : VesselSide;
}

export class BerthReservation {
    public Id : DataSignal<number>;
    public Url : DataSignal<string | null>;
	
    public CargoActivities : SDataArray<CargoActivity>;
    public Change : DataSignal<Change | null>;
    public ChangeId : DataSignal<number>;
    public CruiseActivities : SDataArray<CruiseActivity>;
    public Issues : SDataArray<Issue>;
    public Itinerary : DataSignal<ItineraryPoint | null>;
    public OthersIssues : SDataArray<Issue>;
    public RaftedVessels : SDataArray<RaftedVessel>;
    public Self : DataSignal<BerthReservationSelf | null>;
    public Version : DataSignal<BerthReservationVersion | null>;
	
    public ApprovedDate : DataSignal<TZDate | null>;
    public CancellationRemarks : DataSignal<string | null>;
    public CancelledDate : DataSignal<TZDate | null>;
    public ConflictCount : DataSignal<number>;
    public Footmark : Measurement;
    public Guid : DataSignal<string>;
    public HasLayOpsOnly : DataSignal<boolean>;
    public Length : Measurement;
    public Rafting : DataSignal<boolean>;
    public RequestDate : DataSignal<TZDate>;
    public SideTo : DataSignal<VesselSide>;

    constructor();
    constructor(data : BerthReservationOptions);
    constructor(data: BerthReservationOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
        this.Url = jsonableObservable(S.value<string | null>(data && data.Url !== undefined ? data.Url : null));
	    
        this.CargoActivities = SArray<CargoActivity>([]);
        this.Change = S.value<Change | null>(null);
        this.ChangeId = jsonableObservable(S.value<number>(data && data.ChangeId !== undefined ? data.ChangeId : 0));
        this.CruiseActivities = SArray<CruiseActivity>([]);
        this.Issues = SArray<Issue>([]);
        this.Itinerary = S.value<ItineraryPoint | null>(null);
        this.OthersIssues = SArray<Issue>([]);
        this.RaftedVessels = SArray<RaftedVessel>([]);
        this.Self = S.value<BerthReservationSelf | null>(null);
        this.Version = S.value<BerthReservationVersion | null>(null);
	    
        this.ApprovedDate = jsonableObservable(S.value<TZDate | null>(data && data.ApprovedDate !== undefined ? data.ApprovedDate === null ? null : new TZDate(data.ApprovedDate) : null, TZDate.nullableEq));
        this.CancellationRemarks = jsonableObservable(S.value<string | null>(data && data.CancellationRemarks !== undefined ? data.CancellationRemarks : null));
        this.CancelledDate = jsonableObservable(S.value<TZDate | null>(data && data.CancelledDate !== undefined ? data.CancelledDate === null ? null : new TZDate(data.CancelledDate) : null, TZDate.nullableEq));
        this.ConflictCount = jsonableObservable(S.value<number>(data && data.ConflictCount !== undefined ? data.ConflictCount : 0));
        this.Footmark = data && data.Footmark !== undefined ? new Measurement(data.Footmark) : new Measurement();
        this.Guid = jsonableObservable(S.value<string>(data && data.Guid !== undefined ? data.Guid : '00000000-0000-0000-0000-000000000000'));
        this.HasLayOpsOnly = jsonableObservable(S.value<boolean>(data && data.HasLayOpsOnly !== undefined ? data.HasLayOpsOnly : false));
        this.Length = data && data.Length !== undefined ? new Measurement(data.Length) : new Measurement();
        this.Rafting = jsonableObservable(S.value<boolean>(data && data.Rafting !== undefined ? data.Rafting : false));
        this.RequestDate = jsonableObservable(S.value<TZDate>(data && data.RequestDate !== undefined ? new TZDate(data.RequestDate) : new TZDate(0), TZDate.eq));
        this.SideTo = jsonableObservable(S.value<VesselSide>(data && data.SideTo !== undefined ? data.SideTo : 0));
    }

    static key(obj : BerthReservation) {
        return obj.Id();
    }
    
    static dataKey(obj : BerthReservationOptions) {
        return obj.Id;
    }

    static update(obj : BerthReservation, data : BerthReservationOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
            if (data.Url !== undefined) obj.Url(data.Url);
	        
            if (data.ChangeId !== undefined) obj.ChangeId(data.ChangeId);
	        
            if (data.ApprovedDate !== undefined) obj.ApprovedDate(data.ApprovedDate === null ? null : new TZDate(data.ApprovedDate));
            if (data.CancellationRemarks !== undefined) obj.CancellationRemarks(data.CancellationRemarks);
            if (data.CancelledDate !== undefined) obj.CancelledDate(data.CancelledDate === null ? null : new TZDate(data.CancelledDate));
            if (data.ConflictCount !== undefined) obj.ConflictCount(data.ConflictCount);
            if (data.Footmark !== undefined) Measurement.update(obj.Footmark, data.Footmark);
            if (data.Guid !== undefined) obj.Guid(data.Guid);
            if (data.HasLayOpsOnly !== undefined) obj.HasLayOpsOnly(data.HasLayOpsOnly);
            if (data.Length !== undefined) Measurement.update(obj.Length, data.Length);
            if (data.Rafting !== undefined) obj.Rafting(data.Rafting);
            if (data.RequestDate !== undefined) obj.RequestDate(new TZDate(data.RequestDate));
            if (data.SideTo !== undefined) obj.SideTo(data.SideTo);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/BerthReservation.ts